import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useGetTenants } from "../api/hooks/useRiskThemes";
import { seedTenantFromHostname, setTenantHeader } from "../api/tenantConfig";

interface TenantContextType {
  selectedTenant: string | null;
  hasMultipleTenants: boolean | null;
  setSelectedTenant: (tenantId: string) => void;
  clearTenant: () => void;
  isLoading: boolean;
  error: string | null;
  tenants: string[] | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const [selectedTenant, setSelectedTenant] = useState<string | null>(() =>
    seedTenantFromHostname()
  );
  const [hasMultipleTenants, setHasMultipleTenants] = useState<boolean | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const { data: tenants, isLoading } = useGetTenants();

  useEffect(() => {
    if (tenants) {
      setHasMultipleTenants(tenants.length > 1);

      if (tenants.length === 1) {
        handleSetTenant(tenants[0].split(";")[0]);
      }
    }
  }, [tenants]);

  const handleSetTenant = useCallback((tenantId: string) => {
    try {
      setError(null);
      setSelectedTenant(tenantId);
      setTenantHeader(tenantId);
    } catch (err) {
      setError("Erro ao atualizar o tenant");
      console.error("Erro ao atualizar tenant:", err);
    }
  }, []);

  const clearTenant = useCallback(() => {
    setSelectedTenant(null);
    setTenantHeader(null);
  }, []);

  return (
    <TenantContext.Provider
      value={{
        selectedTenant,
        hasMultipleTenants,
        setSelectedTenant: handleSetTenant,
        clearTenant,
        isLoading,
        error,
        tenants: tenants || null,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
